import { IconButton, Stack, Typography } from '@mui/material';
import { useMemo } from 'react';
import { IChatConversation } from 'src/@types/chat';
import { useAuthContext } from 'src/auth/useAuthContext';
import Iconify from 'src/components/iconify';
import { HeaderIcon } from 'src/layouts/DashboardLayout/shared/styles';
import { useLocales } from 'src/locales';
import { setConversation, setToggleThread } from 'src/redux/slices/chat';
import { dispatch, useSelector } from 'src/redux/store';
import { useChatThreads } from 'src/sections/@dashboard/chat/ChatHeader/ChannelThreads/useChannelThreads';
import { PageHeaderContainer } from '../../../../components/PageHeader/PageHeaderContainer';
import { DeleteThreadPopover } from './DeleteThreadPopover';

export type Props = {
  isStartThread?: boolean;
  conversation?: IChatConversation;
};

export const ThreadHeader = ({ conversation, isStartThread }: Props) => {
  const { user } = useAuthContext();
  const { translate } = useLocales();
  const { refetch } = useChatThreads({
    channelId: conversation?.parentId as string,
  });

  const { conversations } = useSelector((state) => state.chat);

  const isFollowing: boolean = useMemo(() => {
    if (!conversation?.followers?.length || isStartThread) return false;
    return conversation.followers.some((item) => item === user?._id);
  }, [conversation?.followers, user?._id, isStartThread]);

  const handleToggleFollow = async () => {
    if (!conversation || !user) return;
    await dispatch(setToggleThread(conversation?._id, user?._id));
    refetch();
    if (!conversation.parentId) return;
    const parentConversation = conversations.byId[conversation.parentId];
    const updatedChannel = {
      ...parentConversation,
      subchannels: isFollowing
        ? [
            ...(parentConversation.subchannels?.filter(
              (item) => item._id !== conversation._id,
            ) || []),
          ]
        : [...(parentConversation.subchannels || []), conversation],
    };
    dispatch(setConversation(updatedChannel));
  };

  return (
    <PageHeaderContainer>
      <Stack direction="row" alignItems="center" gap={1}>
        <HeaderIcon>
          <Iconify icon="tabler:timeline-event-text" />
        </HeaderIcon>
        <Typography variant="h4">
          {conversation?.threadNumber
            ? translate('communicate.thread.title_enumerated', {
                threadNumber: conversation?.threadNumber,
              })
            : translate('communicate.thread.title')}
        </Typography>
      </Stack>
      <Stack direction="row" alignItems="center" gap={1}>
        {!isStartThread && (
          <IconButton onClick={handleToggleFollow}>
            <Iconify icon={isFollowing ? 'tabler:eye' : 'tabler:eye-closed'} />
          </IconButton>
        )}
        {!!conversation && <DeleteThreadPopover conversation={conversation} />}
      </Stack>
    </PageHeaderContainer>
  );
};
